import React, { useState, useRef, useEffect, memo } from 'react'
import styled from 'styled-components'
import { KeyboardArrowDown } from '@material-ui/icons'
import { Spacings, Text, Devices } from '@styles'

// maps order status to V2 semantic status colors from theme
const getStatusStyles = (status, theme) => {
	const normalized = status ? status.toUpperCase().replace(/\s+/g, '_') : ''
	const s = theme?.colors?.status || {}
	switch (normalized) {
		case 'ORDER_PLACED':
			return { bg: s.info?.bg, border: s.info?.border, color: s.info?.text }
		case 'ACTIVE':
			return { bg: s.warning?.bg, border: s.warning?.border, color: s.warning?.text }
		case 'CANCELLED':
			return { bg: s.error?.bg, border: s.error?.border, color: s.error?.text }
		case 'COMPLETED':
			return { bg: s.success?.bg, border: s.success?.border, color: s.success?.text }
		case 'SHORT_CLOSED':
			// Light yellow — the neutral grey read as a disabled option.
			return { bg: s.warning?.bg, border: s.warning?.border, color: s.warning?.text }
		default:
			return { bg: s.neutral?.bg, border: s.neutral?.border, color: s.neutral?.text }
	}
}

const DropdownWrapper = styled.div`
	position: relative;
	display: inline-block;
	font-family: ${({ theme }) => theme.fontFamily.inter || "'Inter', sans-serif"};
	width: ${({ fullWidthOnMobile }) => (fullWidthOnMobile ? '100%' : 'auto')};

	@media ${Devices.tablet} {
		width: auto;
	}
`

const DefaultButtonTrigger = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: ${({ active, theme }) => (active ? theme.colors.surface.card : theme.colors.brand.primary)};
	border: 1px solid ${({ active, theme }) => (active ? theme.colors.brand.primary : 'transparent')};
	border-radius: ${({ theme }) => theme.radius.full};
	height: ${Spacings.SPACING_8B};
	color: ${({ active, theme }) => (active ? theme.colors.brand.primary : theme.colors.text.inverse)};
	cursor: pointer;
	box-shadow: ${({ theme }) => theme.shadows.xs};
	transition: all 0.2s ease-in-out;
	padding: 0 ${Spacings.SPACING_3B};
	box-sizing: border-box;
	font-family: inherit;
	font-weight: ${Text.BOLD_500};
	gap: ${Spacings.SPACING_2B};

	@media ${Devices.tablet} {
		height: calc(${Spacings.SPACING_8B} + ${Spacings.SPACING_2});
		padding: 0 ${Spacings.SPACING_4B};
	}

	&:hover {
		background: ${({ active, theme }) => (active ? theme.colors.brand.subtle : theme.colors.brand.hover)};
		color: ${({ active, theme }) => (active ? theme.colors.brand.primary : theme.colors.text.inverse)};
		border-color: ${({ active, theme }) => (active ? theme.colors.brand.primary : 'transparent')};
	}
`

const PillTrigger = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	background-color: ${({ status, theme }) => getStatusStyles(status, theme).bg};
	border: 1px solid ${({ status, theme }) => getStatusStyles(status, theme).border};
	color: ${({ status, theme }) => getStatusStyles(status, theme).color};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_700};
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_3B};
	border-radius: ${({ theme }) => theme.radius.full};
	cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
	user-select: none;
	transition: all 0.2s ease;
	width: 100%;
	box-sizing: border-box;

	@media ${Devices.tablet} {
		width: auto;
	}

	&:hover {
		opacity: ${({ $readOnly }) => ($readOnly ? 1 : 0.95)};
		transform: ${({ $readOnly }) => ($readOnly ? 'none' : 'translateY(-1px)')};
		box-shadow: ${({ theme, $readOnly }) => ($readOnly ? 'none' : theme.shadows.sm)};
	}
`

const ArrowIcon = styled.span`
	display: inline-flex;
	align-items: center;
	transition: transform 0.2s ease;
	transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};

	svg {
		font-size: ${Text.LARGE};
		fill: currentColor;
	}
`

const DropdownMenu = styled.div`
	position: absolute;
	top: calc(100% + ${Spacings.SPACING_1B});
	z-index: 500;
	background: ${({ theme }) => theme.colors.surface.card};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	box-shadow: ${({ theme }) => theme.shadows.md};
	display: flex;
	flex-direction: column;
	width: max-content;

	${({ type, theme }) =>
		type === 'pill'
			? `
		left: 50%;
		transform: translateX(-50%);
		border-radius: ${theme.radius.lg};
		padding: ${Spacings.SPACING_2B};
		gap: ${Spacings.SPACING_1B};
		min-width: calc(10.5 * ${Spacings.SPACING_4B});
	`
			: `
		right: 0;
		left: auto;
		border-radius: ${theme.radius.md};
		padding: ${Spacings.SPACING_1B} 0;
		min-width: calc(10 * ${Spacings.SPACING_4B});

		@media ${Devices.tablet} {
			min-width: calc(11.25 * ${Spacings.SPACING_4B});
		}
	`}
`

const DropdownItemPill = styled.div`
	background-color: ${({ status, theme }) => getStatusStyles(status, theme).bg};
	border: 1px solid ${({ status, theme }) => getStatusStyles(status, theme).border};
	color: ${({ status, theme }) => getStatusStyles(status, theme).color};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_700};
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_3B};
	border-radius: ${({ theme }) => theme.radius.full};
	cursor: pointer;
	text-align: center;
	transition: all 0.15s ease;
	user-select: none;
	font-family: inherit;

	&:hover {
		opacity: 0.9;
		transform: scale(1.02);
		box-shadow: ${({ theme }) => theme.shadows.xs};
	}
`

// semantic colors an option can take; anything else falls back to the default text color
const getItemColor = (theme, $color) => {
	switch ($color) {
		case 'danger':
			return theme.colors.status.error.text
		case 'success':
			return theme.colors.status.success.text
		case 'primary':
			return theme.colors.brand.primary
		default:
			return theme.colors.text.primary
	}
}

const DropdownItemButton = styled.button`
	background: none;
	border: none;
	text-align: left;
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	color: ${({ theme, $color }) => getItemColor(theme, $color)};
	cursor: pointer;
	width: 100%;
	font-weight: ${Text.BOLD_500};
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	transition: all 0.2s ease-in-out;
	font-family: inherit;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
		font-size: ${Text.MEDIUM};
	}

	&:hover:not(:disabled) {
		background: ${({ theme, $color }) =>
			$color === 'danger'
				? theme.colors.status.error.bg
				: $color === 'success'
				? theme.colors.status.success.bg
				: theme.colors.brand.subtle};
		color: ${({ theme, $color }) => ($color ? getItemColor(theme, $color) : theme.colors.brand.primary)};
	}

	svg {
		font-size: ${Text.MEDIUM};
		color: ${({ theme, $color }) => ($color ? getItemColor(theme, $color) : theme.colors.text.secondary)};
		transition: color 0.2s ease-in-out;

		@media ${Devices.tablet} {
			font-size: ${Text.LARGE};
		}
	}

	&:hover:not(:disabled) svg {
		color: ${({ theme, $color }) => ($color ? getItemColor(theme, $color) : theme.colors.brand.primary)};
	}

	&:disabled {
		color: ${({ theme }) => theme.colors.text.disabled};
		cursor: not-allowed;
		background: none;

		svg {
			color: ${({ theme }) => theme.colors.text.disabled} !important;
		}
	}

	&.mobile-only {
		display: flex;
		@media (min-width: 1100px) {
			display: none;
		}
	}

	&.desktop-only {
		display: none;
		@media (min-width: 1100px) {
			display: flex;
		}
	}
`

// Escape hatch for options that are not plain menu buttons (a toggle, a switch…): the option supplies
// its own node via `render` and the menu stays open while the user interacts with it.
const DropdownItemCustom = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
	}
`

const DropdownV2 = memo(({
	type = 'button',
	label,
	status,
	// When true the pill renders as a plain, non-interactive status chip (no arrow, no menu).
	readOnly = false,
	active = false,
	trigger,
	options = [],
	onChange,
	fullWidthOnMobile = true,
	className,
	style,
	menuStyle,
	onOpenChange,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const containerRef = useRef(null)

	useEffect(() => {
		if (typeof onOpenChange === 'function') {
			onOpenChange(isOpen)
		}
	}, [isOpen, onOpenChange])

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const handleToggle = () => {
		if (readOnly) return
		setIsOpen((prev) => !prev)
	}

	const handleItemClick = (option, e) => {
		if (typeof onChange === 'function') {
			onChange(option)
		} else if (typeof option.onClick === 'function') {
			option.onClick(e)
		}
		setIsOpen(false)
	}

	return (
		<DropdownWrapper ref={containerRef} fullWidthOnMobile={fullWidthOnMobile} className={className} style={style}>
			{typeof trigger === 'function' ? (
				trigger({ isOpen, onClick: handleToggle })
			) : type === 'pill' ? (
				<PillTrigger status={status || label} $readOnly={readOnly} onClick={handleToggle}>
					<span>{label}</span>
					{!readOnly && (
						<ArrowIcon isOpen={isOpen}>
							<KeyboardArrowDown />
						</ArrowIcon>
					)}
				</PillTrigger>
			) : (
				<DefaultButtonTrigger active={active || isOpen} onClick={handleToggle}>
					{label}
					<ArrowIcon isOpen={isOpen}>
						<KeyboardArrowDown />
					</ArrowIcon>
				</DefaultButtonTrigger>
			)}

			{isOpen && !readOnly && (
				<DropdownMenu type={type} style={menuStyle}>
					{options.map((option, index) => {
						if (option.hide) return null
						if (option.render) {
							return (
								<DropdownItemCustom key={option.key || index}>
									{typeof option.render === 'function' ? option.render({ close: () => setIsOpen(false) }) : option.render}
								</DropdownItemCustom>
							)
						}
						return type === 'pill' ? (
							<DropdownItemPill
								key={option.key || index}
								status={option.label}
								onClick={(e) => handleItemClick(option, e)}
							>
								{option.label}
							</DropdownItemPill>
						) : (
							<DropdownItemButton
								key={option.key || index}
								onClick={(e) => handleItemClick(option, e)}
								disabled={option.disabled}
								className={option.className}
								$color={option.color}
							>
								{option.icon}
								{option.label}
							</DropdownItemButton>
						)
					})}
				</DropdownMenu>
			)}
		</DropdownWrapper>
	)
})

export default DropdownV2
